import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useAuditoriaUtils } from "../hooks/useAuditoriaUtils";
import { useLiquidaciones } from "../contexts/LiquidacionesContext";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EmailIcon from "@mui/icons-material/Email";
import ErrorIcon from "@mui/icons-material/Error";
import FacebookCircularProgress from "../../FacebookCircularProgress";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import { makeStyles } from "@mui/styles";
import { CapSentence } from "../../utils/Utils";
import { useLiquidarUtils } from "../hooks/useLiquidarUtils";

const useStyles = makeStyles({
  columnHeaders: {
    backgroundColor: "#eceff1 !important",
    borderBottom: "1px solid #ccc !important",
  },
  columnHeaderTitle: {
    fontSize: "0.8rem",
    lineHeight: 1,
  },
});

function LinearProgressWithLabel({ value, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <LinearProgress variant="determinate" value={value} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

export default function LiquidacionView() {
  const classes = useStyles();
  const [loadingLiquidaciones, setLoadingLiquidaciones] = useState(false);

  const {
    searchingResumen,
    updateLiquidaciones,
    liquidacionRows: rows,
    liquidandoNombre,
    selectedLiquidacionRows,
    errorRows,
    progreso,
  } = useLiquidaciones();

  const { handleSearchUrlPdf } = useAuditoriaUtils();

  const { fetchLiquidaciones, handleLiquidarSeleccionados } =
    useLiquidarUtils();

  useEffect(() => {
    const cargarLiquidaciones = async () => {
      setLoadingLiquidaciones(true);
      await fetchLiquidaciones();
      setLoadingLiquidaciones(false);
    };

    cargarLiquidaciones();
  }, []);
  const handleVerPdf = async (pdfFilename) => {
    const url = await handleSearchUrlPdf(pdfFilename);
    if (url) window.open(url, "_blank");
  };

  const columns = [
    { field: "odontologo_nombre", headerName: "Odontólogo", width: 250 },
    { field: "facturado", headerName: "Ingresado", width: 150 },
    { field: "pagado", headerName: "A pagar", width: 150 },
    { field: "porcentaje_rechazado", headerName: "% Rechazo", width: 150 },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const isLoading = row.isLoading;
        const hasError = !!errorRows[row.odontologo_id];
        const isLiquidado = !!row.pdf_filename;

        if (isLoading) {
          return (
            <AutorenewIcon
              sx={{
                ml: 1,
                color: "primary.main",
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          );
        }

        if (hasError) {
          return (
            <Tooltip title="Error al liquidar">
              <ErrorIcon color="error" />
            </Tooltip>
          );
        }

        return (
          <>
            {!isLiquidado ? (
              <Tooltip title="Liquidar">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleLiquidarSeleccionados("soloEmitir", [
                      row.odontologo_id,
                    ])
                  }
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Reliquidar">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleLiquidarSeleccionados("soloEmitir", [
                        row.odontologo_id,
                      ])
                    }
                  >
                    <PictureAsPdfIcon color="primary" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver PDF">
                  <IconButton
                    size="small"
                    onClick={() => handleVerPdf(row.pdf_filename)}
                  >
                    <FindInPageIcon color="primary" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Enviar email">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleLiquidarSeleccionados("soloEnviar", [
                        row.odontologo_id,
                      ])
                    }
                  >
                    <EmailIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const rowsWithId = rows.map((r) => ({ ...r, id: r.odontologo_id }));

  return (
    <Box maxWidth="xl" mx="auto" px={2}>
      {loadingLiquidaciones && (
        <Box display="flex" justifyContent="center" my={4}>
          <FacebookCircularProgress />
        </Box>
      )}

      {!loadingLiquidaciones && rowsWithId.length > 0 && (
        <Box mt={2}>
          <Box flexGrow={1} mt={1} minHeight={40}>
            {liquidandoNombre ? (
              <LinearProgressWithLabel
                label={`Procesando: ${CapSentence(liquidandoNombre)}`}
                value={progreso}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sin liquidaciones en curso...
              </Typography>
            )}
          </Box>

          <DataGrid
            rows={rowsWithId}
            columns={columns}
            checkboxSelection
            selectionModel={selectedLiquidacionRows}
            onSelectionModelChange={(ids) =>
              updateLiquidaciones({ selectedLiquidacionRows: ids })
            }
            rowHeight={38}
            disableSelectionOnClick
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "fila-par"
                : "fila-impar"
            }
            classes={{
              columnHeaders: classes.columnHeaders,
              columnHeader: classes.columnHeader,
              columnHeaderTitle: classes.columnHeaderTitle,
            }}
            sx={{
              height: "70vh",
              overflowX: "hidden",
              overflowY: "auto",
              "& .MuiDataGrid-row.fila-par": { backgroundColor: "#e8eaf6" },
              "& .MuiDataGrid-row.fila-impar": { backgroundColor: "#eceff1" },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#c5e1f4 !important",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "#b3d5ee !important",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
